import React, { useContext, useState, useCallback, useRef, useEffect } from 'react';
import { Grid, Cell } from 'styled-css-grid';
import { Icon } from 'semantic-ui-react';
import { AuthZeroApolloContext } from '@src/views/components/util/external/AuthZeroApolloClient';
import { useCentralCalls } from '@src/rest/CentralCalls';

// Chart components
import DashPieChart from './charts/DashPieChart';
import DashLineChart from './charts/DashLineChart';
import DashBarChart from './charts/DashBarChart';
import DashSingleValue from './charts/DashSingleValue';
import DashStackedBarChart from './charts/DashStackedBarChart';
import DashTable from './charts/DashTable';
import AIModel from './charts/AIModel';
import DashFilterList from './charts/DashFilterList';
import DashActivity from './charts/DashActivity';
import StrategicGanttView from './charts/etc/StrategicGanttView/StrategicGanttView';
import DashBudget from './charts/DashBudget';
import html2canvas from 'html2canvas';
import PptxGenJS from 'pptxgenjs';
// Other components
import { DashGridItem } from './DashGrid';
import  SummaryView  from './summaryView/SummaryView';
import AiFloatingResponse from './AiFloatingResponse';
import BuildItem from './charts/builder/BuildItem';
import DashItemLoader from "@src/views/pages/projects/dash/DashItemLoader";
import { createPowerPointSlide } from "../dash/charts/etc/PptUtils"; // Import the function

import './Dash.scss';
import NestedReport from './charts/NestedReport';
import DashAiResponse from './charts/DashAiResponse';
import DashGanttView from './charts/DashGanttView';

export interface DashCardProps {
    item: DashGridItem;
    projectVersionId?: string;
    userId: string;
    editMode?: boolean;
    projectId: string;
    setFilterParams?: (data: any) => void;
    filterParams?: any;
    onSlideReady?: (data: { item: DashGridItem; chartRef?: HTMLDivElement | null; realChartData?: any[] }) => void;
    objectTypeIn?: 'category' | 'subCategory' | 'objective' | 'activity';
    calendarTypeIn?: 'YEAR' | 'QUARTER' | 'MONTH' | 'WEEK';
}

export interface DashChartProps {
    item: DashGridItem;
    projectVersionId?: string;
    userId: string;
    projectId?: string;
    filterParams?: any;
    data?: any;
    chartType?: string;
}

export interface DashFilterProps {
    item: DashGridItem;
    setFilterChange: (data: any) => void;
    projectVersionId?: string;
    userId: string;
    projectId?: string;
    paramName: string;
    filterParams?: any;
    tableName: string;
    displayColumnName: string;
    data: any;
}

const DashCard: React.FC<DashCardProps> = ({ item, projectVersionId, userId, editMode, projectId, setFilterParams, filterParams, onSlideReady }) => {
    const [ showAiResponse, setShowAiResponse ] = useState( false ),
          [ aiResponse, setAiResponse ]         = useState( '' ),
          [ loading, setLoading ]               = useState( true ),
          [ realChartData, setRealChartData ]   = useState( [] );
    const { currentToken } = useContext(AuthZeroApolloContext);
    const { GetAIResponse } = useCentralCalls();
    const chartRef = useRef<HTMLDivElement>(null);
    const SetChartData = async (data: any) => {
        setRealChartData(data);
    }

    const AiButtonClick = useCallback(async () => {
        if (showAiResponse) return;

        setLoading(true);
        setAiResponse('showing?');
        try {
            setLoading(false);
        } catch (error) {
            console.error('Error fetching AI response:', error);
            setAiResponse('Error fetching AI response');
            setLoading(false);
        }
    }, [realChartData, currentToken, showAiResponse]);

    const downloadCSV = () => {
        if (!realChartData || realChartData.length === 0) return;

        const columnNames = Object.keys(realChartData[0]);

        const csvContent = [
            columnNames.join(','), // Header row
            ...realChartData.map(row =>
                columnNames.map(column => JSON.stringify(row[column] ?? '')).join(',')
            )
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.href = url;
        link.setAttribute('download', 'tableData.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };
    // const createPowerPointSlide = async (pptx: PptxGenJS, slideIndex?: number) => {
    //     const slideTitle = item.title?.trim() ? item.title : item.subtitle;
    //     const slide = pptx.addSlide();

    //     // Handle Table Export
    //     if (item.chartType === "table" && realChartData && realChartData.length > 0) {
    //         const columns = Object.keys(realChartData[0]);
    //         const tableRows = realChartData.map(row => columns.map(col => row[col] || ""));
    //         const maxRowsPerSlide = 10;
    //         let startRow = 0;
    //         let slideCount = 1;

    //         while (startRow < tableRows.length) {
    //             const tableSlide = slideCount > 1 ? pptx.addSlide() : slide;
    //             if (slideCount === 1) {
    //                 tableSlide.addText(slideTitle, { x: 0.5, y: 0.3, fontSize: 24, bold: true, align: "center", w: 9 });
    //             }
    //             tableSlide.addTable([columns, ...tableRows.slice(startRow, startRow + maxRowsPerSlide)], {
    //                 x: 0.5, y: slideCount === 1 ? 1.5 : 0.5, w: 9,
    //                 colW: Array(columns.length).fill(1.5),
    //                 border: { type: "solid", color: "000000", pt: 1 },
    //                 fontSize: 12,
    //                 autoPage: true
    //             });

    //             startRow += maxRowsPerSlide;
    //             slideCount++;
    //         }
    //     } else {
    //         // Capture chart as image
    //         if (chartRef.current) {
    //             const canvas = await html2canvas(chartRef.current, { backgroundColor: "#fff" });
    //             const imgData = canvas.toDataURL("image/png");
    //             slide.addText(slideTitle, { x: 0.5, y: 0.3, fontSize: 24, bold: true, align: "center", w: 9 });
    //             slide.addImage({ data: imgData, x: 0.5, y: 1.5, w: 8, h: 5 });
    //         }
    //     }
    // };

    // Export only this card
    const exportToPPT = async () => {
        const pptx = new PptxGenJS();
        await createPowerPointSlide(pptx, item, chartRef.current, realChartData);
        pptx.writeFile({ fileName: `${item.title || "Chart"}.pptx` });
    };
    // const generateSlide = async () => {
    //     if (onSlideReady && (realChartData && realChartData.length > 0) || chartRef.current ) {
    //         console.log('setting slideready...')
    //         onSlideReady({
    //             item,
    //             chartRef: chartRef.current,
    //             realChartData: item.chartType === "table" ? realChartData : [],
    //         });
    //     }
    // };
    const generateSlide = async () => {
        if (onSlideReady) {
            onSlideReady({
                item,
                chartRef: chartRef.current,
                realChartData: item.chartType === "table" ? realChartData : [],
            });
        }
    };
    // useEffect(() => {
    //     if (realChartData.length > 0 || chartRef.current) {
    //         console.log(item);
    //         console.log(realChartData);
    //         generateSlide();
    //     }
    // }, [realChartData]); 
    const setParameterChange = (data) => {
        setFilterParams(data);
    }

    const handleCloseAiResponse = useCallback(() => {
        setShowAiResponse(false);
    }, []);

    return (
        <Grid
            className="dash-card"
            columns={1}
            rows={editMode ? "1fr" : "min-content 1fr"}
            areas={editMode ? ["content"] : ["header", "content"]}
            style={{ height: '100%', width: '100%', overflow: 'hidden' }}>
            {item.showHeaderFooter !== false && editMode === false && item.title && item.title !== '' && (
                <Cell area="header" className="dash-card-header">
                    <div className="dash-card-icon">
                        <Icon
                            name={item.iconName || 'lightbulb'}
                            className="dash-card-icon-element"
                            onClick={AiButtonClick}
                        />

                        {showAiResponse && (
                            <div className="ai-response-container">
                                <AiFloatingResponse
                                    context={'Read my react chart data and comment on the data and any insights you might have about the data. Don\'t mention anything about the format, just focus on the data'}
                                    input={JSON.stringify(realChartData)}
                                    onClose={handleCloseAiResponse}
                                />
                            </div>
                        )}
                    </div>

                    <div className="dash-card-title" title={item.title}>
                        {item.title}
                    </div>

                    <div className="dash-card-actions">
                        {item.chartType === 'table' && (
                            <Icon
                                name="download"
                                className="dash-card-download"
                                onClick={downloadCSV}
                                title="Download as CSV"
                            />
                        )}
                        {['table', 'pie', 'bar', 'stackedBar'].includes(item.chartType) && (
                            <Icon
                                name="file powerpoint"
                                onClick={exportToPPT}
                                className="dash-card-icon-btn"
                                title="Export to Powerpoint"
                            />
                        )}
                    </div>
                </Cell>
            )}

            <Cell area="content" className="dash-card-content" style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                {item.subtitle && item.subtitle !== '' && (<span className='dash-card-subtitle'>{item.subtitle}</span>)}
                <div ref={chartRef} style={{ flex: 1, minHeight: 0, overflow: 'hidden', position: 'relative' }}>
                    {editMode === false ? (
                        <DashItemLoader
                            filterParams={filterParams}
                            userId={userId}
                            projectVersionId={projectVersionId}
                            projectId={projectId}
                            item={item}
                            setChartData={setRealChartData}
                            renderFunctionComponent={(data) => {
                                const componentHash = new Map([
                                    ['pie', { Component: DashPieChart, props: { data, title: item.subtitle, filterParams: filterParams } }],
                                    ['singleValue', { Component: DashSingleValue, props: { chartType: 'singleValue', data, title: item.subtitle, filterParams: filterParams } }],
                                    ['line', { Component: DashLineChart, props: { data, title: item.subtitle, filterParams: filterParams } }],
                                    ['bar', { Component: DashBarChart, props: { data, title: item.subtitle, filterParams: filterParams } }],
                                    ['stackedBar', { Component: DashStackedBarChart, props: { data, title: item.subtitle, filterParams: filterParams } }],
                                    ['table', { Component: DashTable, props: { chartType: 'table', data, title: item.subtitle } }],
                                    ['filterList', { Component: DashFilterList, props: { chartType: 'filterList', data, item, projectVersionId: projectVersionId, userId: userId, filterParams: filterParams, setFilterChange: setParameterChange } }],
                                    ['summaryView', { Component: SummaryView, props: { chartType: 'summaryView', data, item, projectVersionId: projectVersionId, userId: userId, filterParams: filterParams, setFilterChange: setParameterChange } }],
                                    ['aiContent', { Component: DashAiResponse, props: { chartType: 'aiContent', data, item, projectVersionId: projectVersionId, userId: userId, filterParams: filterParams, setFilterChange: setParameterChange, title:"Cor-IQ Insight", systemContext:"You are a dashboard analysis agent. Keep answers concise." } }]
                                ]);

                                const renderComponent = componentHash.get(item.chartType);
                                if (!renderComponent) {
                                    return (
                                        <div style={{
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '8px',
                                            color: 'rgba(0,0,0,0.3)',
                                            textAlign: 'center',
                                            padding: '16px',
                                        }}>
                                            <Icon name="cog" size="large" />
                                            <span style={{ fontSize: '0.8rem' }}>
                                                Not configured — enter edit mode to set up this widget
                                            </span>
                                        </div>
                                    );
                                }

                                const { Component, props } = renderComponent;
                                return <Component {...props} />;
                            }}
                        />
                    ) : (
                        <BuildItem item={item} DeleteGridItem={null} />
                    )}
                </div>
                {!editMode && item.chartType === 'aimodel' && <AIModel />}

                {!editMode && item.chartType === 'activity' && <DashActivity item={item} userId={userId} />}
                {!editMode && item.chartType === 'strategicGanttView' && (
                    <StrategicGanttView
                        filterParams={filterParams}
                        userId={userId}
                        projectVersionId={projectVersionId}
                        projectId={projectId}
                        setChartData={SetChartData}
                        item={item}
                    />
                )}
                {!editMode && item.chartType === 'dashGanttView' && (
                    <DashGanttView
                        filterParams={filterParams}
                        userId={userId}
                        projectVersionId={projectVersionId}
                        projectId={projectId}
                        setChartData={SetChartData}
                        item={item}
                    />
                )}
                {!editMode && item.chartType === 'budget' && (
                    <DashBudget
                        filterParams={filterParams}
                        userId={userId}
                        projectVersionId={projectVersionId}
                        projectId={projectId}
                        setChartData={SetChartData}
                        item={item}
                    />
                )}
                  {!editMode && item.chartType === 'summaryView' && (
                    <SummaryView
                        
                        userId={userId}
                        projectVersionId={projectVersionId}
                        projectId={projectId}
                        item={item}
                    />
                )}
               
                {item.chartType === 'nested' && <NestedReport filterParams={filterParams}  userId={userId} projectVersionId={projectVersionId} projectId={projectId} item={item}  ></NestedReport> }
            </Cell>
        </Grid>
    );
};

export default DashCard;